import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatError } from "@/lib/api";

const b64 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const raw = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };

export const enablePush = async () => {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { toast.error("This browser doesn't support push notifications. On iPhone, add Clockaroo to your Home Screen first."); return; }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { toast.error("Notifications are blocked. Allow them in your browser or phone settings."); return; }
    const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/sw.js"));
    const { data } = await api.get("/push/key");
    const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64(data.public_key) }));
    const j = sub.toJSON();
    await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
    toast.success("Push notifications turned on for this device");
  } catch (e) { toast.error(formatError(e)); }
};

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [load]);
  const open = (o) => { if (o && data.unread) { api.post("/notifications/read").then(() => setData((d) => ({ ...d, unread: 0 }))); } };
  const canPush = typeof Notification !== "undefined" && Notification.permission !== "granted";
  return (
    <Popover onOpenChange={open}>
      <PopoverTrigger className="relative grid h-10 w-10 place-items-center rounded-full border border-slate-200 hover:bg-slate-50" aria-label="Notifications" data-testid="notifications-bell">
        <Bell className="h-5 w-5 text-slate-600" />
        {data.unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white" data-testid="notifications-unread">{data.unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3"><p className="font-semibold">Notifications</p>
          {canPush && <button onClick={enablePush} className="flex items-center gap-1 text-xs font-semibold text-[#00A85C]" data-testid="enable-push-btn"><BellRing className="h-3.5 w-3.5" />Turn on phone alerts</button>}</div>
        <ul className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-500">You're all caught up.</li>}
          {data.items.map((n) => (
            <li key={n.notification_id}><button onClick={() => navigate(n.link)} className={`w-full border-b border-slate-50 px-4 py-3 text-left hover:bg-slate-50 ${n.read ? "" : "bg-emerald-50/50"}`} data-testid={`notification-${n.notification_id}`}>
              <p className="text-sm font-semibold">{n.title}</p><p className="text-xs text-slate-600">{n.body}</p>
              <p className="mt-1 text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</p></button></li>))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
